/* HFA: keep one-time quantity savings separate from Seal subscription prices. */
(() => {
  const prices = new Map();
  let scheduled;
  const originals = new WeakMap();
  const plans = (handle) => {
    if (!prices.has(handle)) prices.set(handle, fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}.js`).then(r => {
      if (!r.ok) throw new Error('Product pricing unavailable');
      return r.json();
    }).then(p => (p.selling_plan_groups || []).flatMap(g => (g.selling_plans || []).map(p => ({...p, group_name:g.name})))));
    return prices.get(handle);
  };
  async function refresh() {
    for (const root of document.querySelectorAll('[data-sugar-offers]')) {
      if (!originals.has(root)) {
        const offers=[...root.querySelectorAll('.offer')];
        if (!offers.some(o=>o.dataset.offerDiscountType==='percentage')) continue;
        originals.set(root,offers.map(o=>({offer:o,type:o.dataset.offerDiscountType,value:o.dataset.offerDiscountValue})));
      }
      const section = root.closest('.shopify-section');
      const input = section?.querySelector('.sealsubs-container input[name="selling_plan"]');
      if (!input || !root.sugarOffers) continue;
      const handle = root.querySelector('.offer')?.dataset.productHandle || location.pathname.split('/products/')[1]?.split('/')[0];
      if (!handle) continue;
      try {
        const all = await plans(handle);
        const selected = String(input.value || '');
        const plan = all.find(p => String(p.id) === selected);
        if (root.dataset.hfaPlan === selected) continue;
        root.dataset.hfaPlan = selected;
        for (const {offer,type,value} of originals.get(root)) {
          offer.dataset.offerDiscountType = plan ? 'none' : type;
          offer.dataset.offerDiscountValue = plan ? '0' : value;
        }
        document.dispatchEvent(new CustomEvent('subscription:type-changed', {detail:{sectionId:section.id.replace(/^shopify-section-/,''), type:plan?'subscribe':'onetime', sellingPlan:plan || null, compareBasis:'onetime', sellingPlanGroupName:plan?.group_name || ''}}));

      } catch (err) { console.warn('HFA pricing could not sync', err); }
    }
  }
  function queue() { clearTimeout(scheduled); scheduled=setTimeout(refresh,120); }
  document.addEventListener('change', e => { if(e.target.closest('.sealsubs-container')) queue(); });
  document.addEventListener('click', e => { if(e.target.closest('.sealsubs-container')) queue(); });
  document.addEventListener('offers:ready', queue);
  document.addEventListener('shopify:section:load', queue);
  if (document.readyState==='loading') document.addEventListener('DOMContentLoaded',queue); else queue();
  // Seal may append its widget after the theme initializes.
  const observer = new MutationObserver(queue);
  observer.observe(document.body,{childList:true,subtree:true});
})();

/* Shipping is based on the discounted order value, not bottle count. */
(() => {
  let pending = false;
  function renderShipping() {
    pending = false;
    const usd = !window.Shopify?.currency?.active || window.Shopify.currency.active === 'USD';
    for (const root of document.querySelectorAll('[data-sugar-offers]')) {
      const handle = root.querySelector('.offer')?.dataset.productHandle || location.pathname.split('/products/')[1]?.split('/')[0];
      const bulky = ['vetcur-vivet-horse','vetcur-beginner-horse','vetcur-yest-horse'].includes(handle);
      let note = root.nextElementSibling;
      if (!note?.classList.contains('hfa-shipping-threshold-note')) {
        note = document.createElement('p');
        note.className = 'hfa-shipping-threshold-note';
        note.textContent = 'Free U.S. shipping on qualifying orders $200 USD or more after discounts—mix and match products. ViVet Horse, Beginner Horse and Yest Horse: contiguous U.S. only; other destinations have shipping charges at checkout.';
        note.style.cssText = 'font:inherit;font-size:.875em;line-height:1.5;margin:10px 0 14px;';
        root.insertAdjacentElement('afterend', note);
      }
      for (const offer of root.querySelectorAll('.offer')) {
        const subtitle = offer.querySelector('.offer__subtitle');
        if (!subtitle) continue;
        let badge = offer.querySelector('.hfa-free-shipping-badge');
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'hfa-free-shipping-badge';
          badge.textContent = bulky ? 'Free contiguous-U.S. shipping' : 'Free U.S. shipping';
          badge.style.cssText = 'display:block;width:fit-content;margin-top:8px;padding:4px 8px;border:1px solid currentColor;border-radius:4px;font-size:.8em;font-weight:700;line-height:1.4;';
          let extras = offer.querySelector('.offer__extras');
          if (!extras) { extras = document.createElement('div'); extras.className='offer__extras'; offer.querySelector('.offer__content').append(extras); }
          extras.prepend(badge);
        }
        const total = Number(offer.dataset.computedTotalPrice ?? offer.dataset.price);
        const eligible = usd && Number.isFinite(total) && total >= 20000;
        badge.hidden = !eligible;
        // Inline display must respect the hidden state on themes that reset [hidden].
        badge.style.display = eligible ? 'block' : 'none';
      }
    }
  }
  function queueShipping() {
    if (!pending) { pending = true; requestAnimationFrame(renderShipping); }
  }
  document.addEventListener('offers:prices-updated', queueShipping);
  document.addEventListener('offers:ready', queueShipping);
  document.addEventListener('shopify:section:load', queueShipping);
  const start = () => {
    queueShipping();
    new MutationObserver(queueShipping).observe(document.body, {
      subtree:true, childList:true, attributes:true, attributeFilter:['data-computed-total-price']
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();

(() => {
  let pending;
  function compactSubscriptionDetails() {
    for (const seal of document.querySelectorAll('.sealsubs-container')) {
      const original = seal.querySelector('.sls-more-info-container');
      const source = original?.querySelector('.sls-more-info-popup-content');
      if (!source) continue;
      let details = seal.querySelector('.hfa-subscription-details');
      if (!details) {
        details = document.createElement('details');
        details.className = 'hfa-subscription-details';
        details.style.cssText = 'display:block;margin-top:12px;border-top:1px solid color-mix(in srgb,currentColor 20%,transparent);font:inherit;font-size:.875em;line-height:1.6;color:inherit;';
        const summary = document.createElement('summary');
        summary.textContent = original.querySelector('.sls-subscription-details-label')?.textContent.trim() || 'Subscription details';
        summary.style.cssText = 'display:list-item;list-style:disclosure-closed inside;cursor:pointer;font:inherit;font-weight:600;min-height:44px;padding:10px 0;box-sizing:border-box;';
        details.append(summary);
        const body = document.createElement('div');
        body.className = 'hfa-subscription-details-body';
        body.style.cssText = 'padding:0 0 12px;';
        const terms = document.createElement('div');
        terms.className = 'hfa-subscription-terms';
        terms.append(...[...source.childNodes].map(node => node.cloneNode(true)));
        body.append(terms);
        details.append(body);
        original.insertAdjacentElement('afterend',details);
        details.addEventListener('toggle', () => {
          summary.style.listStyleType = details.open ? 'disclosure-open' : 'disclosure-closed';
        });
      }
      if (original.style.display !== 'none') original.style.display = 'none';
      const note = seal.querySelector('.hfa-dog-bottle-note');
      const body = details.querySelector('.hfa-subscription-details-body');
      if (note && note.parentElement !== body) body.append(note);
    }
  }
  function queueDetails() {
    clearTimeout(pending);
    pending = setTimeout(compactSubscriptionDetails,80);
  }
  const start = () => {
    queueDetails();
    new MutationObserver(records => {
      if (records.some(record => record.addedNodes.length || record.removedNodes.length)) queueDetails();
    }).observe(document.body,{childList:true,subtree:true});
  };
  document.addEventListener('shopify:section:load',queueDetails);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',start); else start();
})();

/* HFA horse offers: selected-container totals, with per-template supply inputs. */
(() => {
  const products = new Map();
  let timer;
  function setText(node, text) { if (node.textContent !== text) node.textContent = text; }
  function line(label, name) {
    let node = label.querySelector('.' + name);
    if (!node) { node = document.createElement('p'); node.className = name; node.style.cssText='margin:5px 0 0;font:inherit;line-height:1.4;'; label.append(node); }
    return node;
  }
  async function refresh() {
    const marker = document.getElementById('hfa-horse-supply');
    const section = marker?.closest('.shopify-section');
    const root = section?.querySelector('[data-sugar-offers]');
    const selected = root?.querySelector('.offer.is-selected');
    let config={name:'DiVet',unit:'bottle',periods:[{min:50,max:50,label:'per horse'},{min:100,max:100,label:'per pony'}],contiguousOnly:false};
    const source=section?.querySelector('[data-hfa-horse-supply-config]');
    if(source){try{config=JSON.parse(source.textContent);}catch(error){console.warn('Horse supply inputs unavailable',error);return;}}
    const seal = section?.querySelector('.sealsubs-container');
    if (!selected || !seal) return;
    section.classList.add('hfa-horse-purchase');
    if (!section.querySelector('[data-hfa-horse-total-style]')) {
      const style=document.createElement('style'); style.dataset.hfaHorseTotalStyle='';
      style.textContent='.hfa-horse-purchase .sealsubs-container .sls-price-container{display:none!important}.hfa-horse-purchase .hfa-horse-order-total{font-weight:700!important}.hfa-horse-purchase .hfa-horse-order-saving{font-size:.85em!important;color:#234334}';section.append(style);
    }
    const handle = selected.dataset.productHandle;
    if (!products.has(handle)) products.set(handle,fetch(`${window.Shopify?.routes?.root||'/'}products/${encodeURIComponent(handle)}.js`).then(r=>{if(!r.ok)throw Error('Pricing unavailable');return r.json();}));
    try {
      const product = await products.get(handle);
      // Read selection again after the request, so a newer click always wins.
      const offer = root.querySelector('.offer.is-selected');
      const quantity = Number(offer.dataset.quantity);
      const variant = product.variants.find(v=>String(v.id)===String(offer.dataset.variantId));
      if (!variant || !quantity) return;
      const currency=window.Shopify?.currency?.active||'USD';
      const money = n=>new Intl.NumberFormat(document.documentElement.lang||'en-US',{style:'currency',currency}).format(n/100);
      const rate = quantity===2?5:quantity===3?10:0;
      for (const option of seal.querySelectorAll('.sls-option-container')) {
        const label=option.querySelector('.sls-label-container'); if(!label)continue;
        const subscription=!!option.querySelector('input[data-type="subscription"]');
        const id=option.querySelector('select')?.value;
        const allocation=(variant.selling_plan_allocations||[]).find(p=>String(p.selling_plan_id)===String(id)) || (!id?(variant.selling_plan_allocations||[])[0]:null);
        if(subscription&&!allocation)continue;
        const total=subscription?allocation.price*quantity:(variant.price-Math.floor(variant.price*rate/100))*quantity;
        const original=variant.price*quantity, saved=original-total;
        setText(line(label,'hfa-horse-order-total'),`${quantity} ${config.name} ${config.unit}${quantity===1?'':'s'} · ${money(total)} ${subscription?'each delivery':'one-time total'}`);
        setText(line(label,'hfa-horse-order-saving'),saved>0?`Save ${money(saved)} (${Math.round(saved/original*100)}%)`:'');
        const daily=(config.periods||[]).map(period=>{const low=money(total/(period.max*quantity));const high=money(total/(period.min*quantity));return `${low}${low===high?'':'–'+high}/day ${period.label}`;}).join(' · ');
        const dailyLine=line(label,'hfa-horse-order-daily');setText(dailyLine,daily?'Approx. '+daily:'');dailyLine.hidden=!daily;
        setText(line(label,'hfa-horse-order-shipping'),currency==='USD'?(total>=20000?(config.contiguousOnly?'Qualifies for free contiguous-U.S. shipping':'Qualifies for free U.S. shipping'):`${money(20000-total)} away from the $200 free-shipping threshold`):'Shipping calculated at checkout');
      }
      const heading=seal.querySelector('.sls-purchase-options-label');if(heading)setText(heading,`Purchase options for ${quantity} ${config.unit}${quantity===1?'':'s'}`);
    } catch(error) { console.warn('Horse purchase totals could not refresh',error); }
  }
  function queue(){clearTimeout(timer);timer=setTimeout(refresh,100);}
  document.addEventListener('click',queue);document.addEventListener('change',queue);
  document.addEventListener('offers:prices-updated',queue);document.addEventListener('shopify:section:load',queue);
  const start=()=>{queue();new MutationObserver(queue).observe(document.body,{subtree:true,childList:true,attributes:true,attributeFilter:['class','value']});};
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',start);else start();
})();
